import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import ClinicPage from "@/pages/ClinicPage";
import LoginPage from "@/pages/LoginPage";
import PatientArea from "@/pages/PatientArea";
import StaffPage from "@/pages/StaffPage";
import AdminPage from "@/pages/AdminPage";
import VideoRoom from "@/pages/VideoRoom";
import PaymentPage from "@/pages/PaymentPage";
import ProfilePage from "@/pages/ProfilePage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import { VerifyEmailPage, ResetPasswordPage, SandboxIdPPage, OnboardingPage } from "@/pages/AuthPages";
import DiditResultPage from "@/pages/DiditResultPage";
import CafePage from "@/pages/CafePage";
import PartnersPage from "@/pages/PartnersPage";
import LegalPage from "@/pages/LegalPage";
import WalletScanPage from "@/pages/WalletScanPage";
import ReschedulePage from "@/pages/ReschedulePage";
import CompensationPage from "@/pages/CompensationPage";

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    const linkId = sessionStorage.getItem("selvix_link_request");
    sessionStorage.removeItem("selvix_link_request");
    const headers = { "X-Session-ID": sid, ...(linkId ? { "X-Link-Request": linkId } : {}) };
    api
      .post("/auth/google/session", {}, { headers })
      .then(async ({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", location.pathname + location.search);
        if (data.linked) toast.success("Google collegato al tuo account");
        const { data: prof } = await api.get("/profile");
        const dest = prof.onboarding_done ? location.pathname + location.search : "/onboarding";
        navigate(dest, { replace: true, state: { user: data } });
      })
      .catch((e) => {
        toast.error(errMsg(e));
        navigate(linkId ? "/profilo?tab=accesso" : "/accedi", { replace: true });
      });
  }, [location, navigate, setUser]);
  return null;
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) {
    return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="auth-loading">Verifica accesso…</div>;
  }
  if (!user) return <Navigate to={`/accedi?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<ClinicPage />} />
      <Route path="/accedi" element={<LoginPage />} />
      <Route path="/cafe" element={<CafePage />} />
      <Route path="/partner" element={<PartnersPage />} />
      <Route path="/note-legali" element={<LegalPage />} />
      <Route path="/wallet/q/:qid" element={<Protected><WalletScanPage /></Protected>} />
      <Route path="/accedi/sandbox/:provider" element={<SandboxIdPPage />} />
      <Route path="/verifica-email" element={<VerifyEmailPage />} />
      <Route path="/verifica-identita/esito" element={<Protected><DiditResultPage /></Protected>} />
      <Route path="/reimposta-password" element={<ResetPasswordPage />} />
      <Route path="/onboarding" element={<Protected><OnboardingPage /></Protected>} />
      <Route path="/profilo" element={<Protected><ProfilePage /></Protected>} />
      <Route path="/area-personale" element={<Protected><PatientArea /></Protected>} />
      <Route path="/staff" element={<Protected roles={["medico", "infermiere", "segreteria", "admin", "super_admin"]}><StaffPage /></Protected>} />
      <Route path="/admin" element={<Protected roles={["admin", "super_admin"]}><AdminPage /></Protected>} />
      <Route path="/superadmin" element={<Protected roles={["super_admin"]}><SuperAdminPage /></Protected>} />
      <Route path="/video/:aid" element={<Protected><VideoRoom /></Protected>} />
      <Route path="/riprogramma/:aid" element={<Protected><ReschedulePage /></Protected>} />
      <Route path="/compenso/:aid" element={<Protected><CompensationPage /></Protected>} />
      <Route path="/pagamento/:pid" element={<Protected><PaymentPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    document.title = "Doctor Selvix | La clinica virtuale italiana";
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
